
import React, { useState, useEffect, useRef } from "react";
import { Search, Loader2, CheckCircle2 } from "lucide-react";
import Cookies from "js-cookie";
import api from "../../../chat/api"; // Ensure path is correct based on your folder structure
import Checkbox from "../../../../Checkbox"; // Ensure path is correct

const normalizeMember = (member) => {
  const displayName = member.full_name || member.email || "Unknown";

  const nameParts = displayName.trim().split(/\s+/);

  let initials = "";
  if (nameParts.length > 0) {
    const firstInitial = nameParts[0][0] || "";
    const lastInitial =
      nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : "";
    initials = (firstInitial + lastInitial).toUpperCase();
  } else {
    initials = "??";
  }

  return {
    id: member.user_id,
    name: displayName,
    role: member.permission_role || "Viewer",
    initials: initials,
    color: "bg-[#8B5CF6]",
  };
};

const AssignTeammateModal = ({ isOpen, onClose, position, onAssign }) => {
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  
  // Status: "idle" | "assigning" | "success"
  const [assignStatus, setAssignStatus] = useState("idle");

  const [members, setMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  const modalRef = useRef(null);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSearch("");
      setSelectedIds([]);
      setAssignStatus("idle");
    }
  }, [isOpen]);

  // Fetch Members Logic
  useEffect(() => {
    const fetchMembers = async () => {
      if (!isOpen) return;

      setIsLoadingMembers(true);
      try {
        const token = Cookies.get("access_token");
        if (!token) {
          console.error("No token available");
          return;
        }

        const pathname = window.location.pathname || "";
        const match = pathname.match(/\/project\/(\d+)\/org\/(\d+)(?:\/|$)/);

        const urlFolderId = match?.[1];
        const organization_id = match?.[2];
        const resolvedFolderId = urlFolderId;

        if (!resolvedFolderId || !organization_id) {
          console.error("folderId or organization_id not found in URL");
          return;
        }

        const response = await api.get(
          `/api/v1/rfp/folders/${resolvedFolderId}/section-view/members/list/?organization_id=${organization_id}&include_org_members=true`
        );

        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
          ? response.data.results
          : [];

        setMembers(list.map(normalizeMember));
      } catch (error) {
        console.error("Error fetching organization members:", error);
      } finally {
        setIsLoadingMembers(false);
      }
    };

    fetchMembers();
  }, [isOpen]);

  if (!isOpen || !position) return null;

  const filteredTeammates = members.filter((member) =>
    member.name.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleAssignClick = async () => {
    setAssignStatus("assigning");
    
    // Call parent handler and wait for result
    const success = await onAssign(selectedIds);

    if (success) {
      setAssignStatus("success");
      // Wait 2 seconds then close
      setTimeout(() => {
        onClose();
        // Reset status happens via the useEffect dependency on isOpen
      }, 2000);
    } else {
      setAssignStatus("idle"); // Revert to allow retry if failed
    }
  };

  return (
    <div
      ref={modalRef}
      className="fixed z-50 rounded-xl shadow-[0px_8px_30px_rgba(0,0,0,0.12)] border border-gray-100 w-[340px] animate-in fade-in zoom-in-95 duration-200 font-outfit overflow-hidden"
      style={{
        top: position.top + 10,
        left: position.left - 300,
        background:
          "linear-gradient(180deg, rgba(206, 173, 255, 0.2) 0%, #FFFFFF 100%), #FFFFFF",
      }}
    >
      <div className="p-5">
        <div className="text-center mb-4">
          <h3 className="text-[16px] font-semibold text-[#18181B]">
            Assign to a Teammate
          </h3>
        </div>

        <div className="relative mb-4">
          <Search
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#18181B]"
            size={18}
            strokeWidth={2.5}
          />
          <input
            type="text"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-11 min-h-[44px] pl-10 pr-4 text-[14px] leading-[20px] hover:text-[14px] focus:text-[14px] bg-white border border-gray-200 rounded-lg text-gray-700 placeholder-gray-400 focus:outline-none focus:border-[#C95EBE] focus:ring-1 focus:ring-[#C95EBE] shadow-sm transition-[border-color,box-shadow]"
            style={{ fontSize: "14px", lineHeight: "20px" }}
          />
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-1 mb-5 shadow-sm">
          <div className="max-h-[200px] overflow-y-auto custom-scrollbar pr-1">
            {isLoadingMembers ? (
              <div className="py-8 flex justify-center items-center text-gray-400">
                <Loader2 size={24} className="animate-spin text-[#C4A5E8]" />
              </div>
            ) : (
              <>
                {filteredTeammates.map((member) => (
                  <div
                    key={member.id}
                    onClick={() => toggleSelection(member.id)}
                    className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg cursor-pointer group transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-full ${member.color} text-white flex items-center justify-center text-[13px] font-medium shrink-0`}
                      >
                        {member.initials}
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-[#18101C] leading-tight">
                          {member.name}
                        </p>
                        <p className="text-[13px] text-gray-500 leading-tight mt-1">
                          {member.role}
                        </p>
                      </div>
                    </div>

                    <Checkbox
                      checked={selectedIds.includes(member.id)}
                      onChange={() => {}}
                      id={`member-${member.id}`}
                      name={`member-${member.id}`}
                      size="md"
                      showLabel={false}
                      selectionMode="multiple"
                    />
                  </div>
                ))}

                {!isLoadingMembers && filteredTeammates.length === 0 && (
                  <div className="py-8 text-center text-gray-400 text-sm">
                    No teammates found
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="flex justify-center">
          <button
            onClick={handleAssignClick}
            disabled={selectedIds.length === 0 || assignStatus !== "idle"}
            className={`
              w-[120px] py-2 rounded-md text-[14px] font-semibold transition-all shadow-sm flex items-center justify-center gap-2
              ${assignStatus === "success" 
                ? "bg-green-600 hover:bg-green-700 text-white" 
                : "bg-[#8B55DD] text-white disabled:opacity-70 disabled:cursor-not-allowed"
              }
            `}
          >
            {assignStatus === "assigning" ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Assigning...
              </>
            ) : assignStatus === "success" ? (
              <>
                <CheckCircle2 size={16} /> Assigned
              </>
            ) : (
              "Assign"
            )}
          </button>
        </div>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: #e4e4e7;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: #d4d4d8;
        }
      `}</style>
    </div>
  );
};

export default AssignTeammateModal;
